import "./RequestsTable.scss";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { CellProps, Column } from "react-table";
import { useLocalStorage } from "usehooks-ts";

import { useMediaQueryContext } from "../../contexts/mediaQueryProvider/MediaQueryProvider";
import type { IOrganization } from "../../contexts/organizations/organizationsContext";
import { useUser } from "../../contexts/userContext";
import { useApplications } from "../../hooks/reactQueryHooks/useApplications";
import { useQueryParams } from "../../hooks/useQueryParams";
import { Filters } from "../../pages/requests/components/Filters";
import { Search } from "../../pages/requests/components/Search";
import type { IData } from "../../types";
import {
  formatMilliseconds,
  formatNumber,
} from "../../utils/NumbersFormatting";
import {
  CacheColumn,
  CacheStatusViewColumn,
  DateColumn,
  DefaultColumn,
  ModelColumn,
  RequestColumn,
  StatusColumn,
} from "../CustomizedTable/Columns";
import { CostColumn } from "../CustomizedTable/Columns/CostColumn";
import { TypeColumn } from "../CustomizedTable/Columns/TypeColumn";
import { PaginatedTable } from "../CustomizedTable/PaginatedTable";
import { RequestSidebar } from "../RequestSidebar";
import { TableCellSm, TableLineSm } from "../TableLineSm";

interface IProps {
  items: IData[];
  totalItems?: number;
  isLoading?: boolean;
  currentOrganization: IOrganization | undefined;
  isSingleCacheItem?: boolean;
  pageSize: number;
}

const RequestsTable: React.FC<IProps> = ({
  items,
  totalItems = 0,
  isLoading = false,
  currentOrganization,
  isSingleCacheItem,
  pageSize,
}) => {
  const { data: applications = [] } = useApplications(
    currentOrganization?.id || "",
  );

  const [hiddenColumns] = useLocalStorage<Record<string, string[]>>(
    "hiddenColumns",
    {},
  );

  const { queryValue: pageIndex, handleChange } = useQueryParams({
    queryStr: "p",
  });

  const { user } = useUser();

  const { isLg } = useMediaQueryContext();

  const [tableData, setTableData] = useState<IData[]>([]);
  const [sidebarData, setSidebarData] = useState<IData | null>(null);

  const currentUserHiddenColumns = hiddenColumns[user?.sub || ""];

  const handleRowClick = (data: IData) => {
    setSidebarData(data);
  };

  const onNextClick = () => {
    const index = sidebarData?.index + 1 || 0;
    if (index < items.length) {
      setSidebarData({
        ...items[index],
        index,
      });
    } else {
      handleChange(`${pageIndex ? Number(pageIndex) + 1 : 2}`, true);
    }
  };

  const onPrevClick = () => {
    if (sidebarData?.index === 0) {
      handleChange(
        `${pageIndex && Number(pageIndex) > 1 ? Number(pageIndex) - 1 : 1}`,
        true,
      );
    } else {
      const index = sidebarData?.index - 1 || 0;
      setSidebarData({
        ...items[index],
        index,
      });
    }
  };

  const onCloseClick = () => {
    setSidebarData(null);
  };

  useEffect(() => {
    setTableData(items);
  }, [items]);

  // set Sidebar data after fetch new
  useEffect(() => {
    if (sidebarData) {
      const index = sidebarData.index === 0 ? pageSize - 1 : 0;
      setSidebarData({
        ...items[index],
        index,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const renderStatus = useCallback(({ row }: CellProps<IData>) => {
    return <StatusColumn statusCode={row.original.response.statusCode} />;
  }, []);

  const renderDate = useCallback(({ row }: CellProps<IData>) => {
    return <DateColumn date={row.original.request.timestamp} type="long" />;
  }, []);

  const renderRequest = useCallback(
    (props: CellProps<IData>) => {
      return <RequestColumn {...props} applications={applications} />;
    },
    [applications],
  );

  const renderCachedStatus = useCallback(({ row }: CellProps<IData>) => {
    return (
      <CacheStatusViewColumn
        response={row.original.response}
        cacheType={row.original.cacheType}
      />
    );
  }, []);

  const renderModel = useCallback(({ row }: CellProps<IData>) => {
    return (
      <ModelColumn
        modelType={row.original.response.model}
        modelVersion={row.original.response.modelVersion}
      />
    );
  }, []);

  const renderType = useCallback(({ row }: CellProps<IData>) => {
    return <TypeColumn type={row?.original?.response?.promptType} />;
  }, []);

  const renderCache = useCallback(({ row }: CellProps<IData>) => {
    return (
      <CacheColumn
        id={row.original?.cacheItemId}
        headers={row.original?.request.headers}
        applicationId={row.original?.applicationId}
      />
    );
  }, []);

  const renderCost = useCallback(({ row }: CellProps<IData>) => {
    return (
      <CostColumn
        prices={row?.original?.prices}
        isFree={row?.original.isFree}
      />
    );
  }, []);

  const renderDefault = useCallback((data: string) => {
    return <DefaultColumn data={data} />;
  }, []);

  const columns: Column<IData>[] = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        width: 70,
        Cell: renderStatus,
      },
      {
        Header: "Date",
        accessor: "date",
        width: 120,
        Cell: renderDate,
      },
      {
        Header: "Prompt",
        accessor: "request",
        Cell: renderRequest,
      },
      {
        Header: "Source",
        accessor: "source",
        Cell: renderCachedStatus,
      },
      {
        Header: "Type",
        accessor: "type",
        Cell: renderType,
      },
      {
        Header: "Model",
        accessor: "model",
        width: 120,
        Cell: renderModel,
      },
      {
        Header: "Cache",
        accessor: "cache",
        width: 120,
        Cell: renderCache,
      },
      {
        Header: "Cost",
        accessor: "cost",
        width: 120,
        Cell: renderCost,
      },
      {
        Header: "Latency",
        accessor: "latency",
        width: 120,
        Cell: props =>
          renderDefault(
            formatNumber(
              Math.round(props.row.original.response.totalTime * 1000),
            ) + "ms",
          ),
      },
      {
        Header: "Total tokens",
        accessor: "totalTokens",
        width: 80,
        Cell: props =>
          renderDefault(formatNumber(props.row.original.response.totalTokens)),
      },
      {
        Header: "Prompt tokens",
        accessor: "promptTokens",
        width: 80,
        Cell: props =>
          renderDefault(formatNumber(props.row.original.response.promptTokens)),
      },
      {
        Header: "Completion tokens",
        accessor: "completionTokens",
        width: 80,
        Cell: props =>
          renderDefault(
            formatNumber(props.row.original.response.completionTokens),
          ),
      },
    ],
    [
      renderStatus,
      renderDate,
      renderRequest,
      renderCachedStatus,
      renderType,
      renderModel,
      renderCache,
      renderDefault,
      renderCost,
    ],
  );

  const renderSM = useCallback(
    (props: CellProps<IData>) => {
      return (
        <TableLineSm
          title={
            <div className="d-flex w-auto align-items-center">
              {renderRequest(props)}
            </div>
          }
        >
          {!currentUserHiddenColumns?.includes("source") && (
            <TableCellSm title="Source">
              {renderCachedStatus(props)}
            </TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("type") && (
            <TableCellSm title="Type">{renderType(props)}</TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("cost") && (
            <TableCellSm title="Cost">{renderCost(props)}</TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("latency") && (
            <TableCellSm title="Latency">
              {renderDefault(
                formatMilliseconds(
                  Math.round(
                    Math.max(props.row.original.response.totalTime * 1000, 1),
                  ),
                ),
              )}
            </TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("status") && (
            <TableCellSm title="Status">{renderStatus(props)}</TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("date") && (
            <TableCellSm title="Date">{renderDate(props)}</TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("model") && (
            <TableCellSm title="Model">{renderModel(props)}</TableCellSm>
          )}

          {!currentUserHiddenColumns?.includes("cache") && (
            <TableCellSm title="Cache">{renderCache(props)}</TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("totalTokens") && (
            <TableCellSm title="Total tokens">
              {renderDefault(props.row.original.response.totalTokens)}
            </TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("promptTokens") && (
            <TableCellSm title="Prompt tokens">
              {renderDefault(props.row.original.response.promptTokens)}
            </TableCellSm>
          )}
          {!currentUserHiddenColumns?.includes("completionTokens") && (
            <TableCellSm title="Completion tokens">
              {renderDefault(props.row.original.response.completionTokens)}
            </TableCellSm>
          )}
        </TableLineSm>
      );
    },
    [
      renderCache,
      renderCachedStatus,
      renderDate,
      renderModel,
      renderRequest,
      renderStatus,
      renderDefault,
      renderCost,
      renderType,
      currentUserHiddenColumns,
    ],
  );

  const columnsSm: Column<IData>[] = [
    {
      Header: "Requests",
      accessor: "request",
      Cell: renderSM,
    },
  ];

  return (
    <>
      {!isSingleCacheItem && <Search />}
      <Filters
        isSingleCacheItem={isSingleCacheItem}
        applications={applications}
        columns={columns}
      />
      <PaginatedTable
        columns={isLg ? columnsSm : columns}
        data={tableData}
        totalItems={totalItems}
        isLoading={isLoading}
        hiddenColumns={currentUserHiddenColumns}
        onRowClick={handleRowClick}
        activeIndex={sidebarData?.index}
      />
      {sidebarData && (
        <RequestSidebar
          sidebarData={sidebarData}
          isFirstItem={
            sidebarData.index === 0 && (Number(pageIndex) === 1 || !pageIndex)
          }
          isLastItem={
            totalItems === 1 ||
            (sidebarData.index === items.length - 1 &&
              Number(pageIndex || 1) * pageSize >= totalItems)
          }
          onNextClick={onNextClick}
          onPrevClick={onPrevClick}
          onClose={onCloseClick}
          isLoading={isLoading}
          applications={applications}
        />
      )}
    </>
  );
};

export default RequestsTable;
